import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * DangerZoneSection — owner-only vessel deletion with type-the-name guard.
 * Backend: DELETE /api/vessels/{id} cascades entries, trips, fuel-economy
 * rows and pending invitations.
 */
export default function DangerZoneSection({ vessel, isOwner }) {
    const nav = useNavigate();
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    if (!isOwner || !vessel) return null;

    const match = confirmText.trim() === (vessel.name || "").trim();

    const doDelete = async () => {
        if (!match || busy) return;
        setBusy(true);
        setErr("");
        try {
            await api.delete(`/vessels/${vessel.id}`);
            nav("/app", { replace: true });
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete the vessel. Please try again.");
            setBusy(false);
        }
    };

    return (
        <section
            className="bm-card"
            data-testid="danger-zone-section"
            style={{ padding: 22, marginTop: 22, border: "1px solid rgba(248, 113, 113, 0.35)" }}
        >
            <h2 className="bm-serif" style={{ fontSize: 22, margin: "0 0 6px", fontWeight: 500, display: "flex", alignItems: "center", gap: 10, color: "var(--bm-bad, #f87171)" }}>
                <AlertTriangle size={18} /> Danger zone
            </h2>
            <p style={{ color: "var(--bm-text-muted)", fontSize: 13, lineHeight: 1.6, margin: "0 0 14px" }}>
                Deleting <strong style={{ color: "var(--bm-text)" }}>{vessel.name}</strong> permanently removes
                <strong> all of its data</strong> — every entry, receipt and photo, the full cruising log,
                fuel-economy history, and co-captain access. This cannot be undone.
            </p>
            {err && (
                <div data-testid="delete-vessel-error" style={{ marginBottom: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)", color: "var(--bm-bad, #f87171)", fontSize: 13 }}>
                    {err}
                </div>
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <input
                    data-testid="delete-vessel-confirm-input"
                    className="bm-input"
                    placeholder={`Type "${vessel.name}" to confirm`}
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    style={{ flex: "1 1 220px", maxWidth: 320 }}
                />
                <button
                    type="button"
                    data-testid="delete-vessel-btn"
                    className="bm-btn bm-btn-ghost"
                    disabled={!match || busy}
                    onClick={doDelete}
                    style={{ padding: "10px 16px", color: "var(--bm-bad, #f87171)", borderColor: "rgba(248,113,113,0.4)", opacity: !match || busy ? 0.5 : 1 }}
                >
                    {busy ? <Loader2 size={14} className="bm-spin" /> : <Trash2 size={14} />} Delete this vessel
                </button>
            </div>
        </section>
    );
}
